import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig, Method } from 'axios';
import { CookieService, SessionService } from 'features/auth/services/CookieService';

export type TravelIntelligenceRequest = {
	method?: Method;
	path: string;
	query?: Record<string, unknown>;
	body?: unknown;
};

export type TravelIntelligenceError = {
	code?: string;
	message: string;
	details?: unknown;
	status?: number;
};

export type DestinationOption = {
	label: string;
	name: string;
	countryCode?: string;
	region?: string;
	lat: number;
	lon: number;
	type?: LocationAutocompleteType | 'coordinates' | 'preset';
	source?: string;
	confidence?: LocationAutocompleteConfidence;
};

export type LocationAutocompleteType = 'country' | 'city' | 'neighborhood';

export type LocationAutocompleteConfidence = 'low' | 'medium' | 'high';

export type LocationAutocompleteItem = {
	id: string;
	type: LocationAutocompleteType;
	name: string;
	fullName: string;
	countryCode?: string;
	lat: number;
	lng: number;
	source: string;
	confidence: LocationAutocompleteConfidence;
};

export type LocationsAutocompleteResponse = {
	items: LocationAutocompleteItem[];
	metadata: {
		source: string;
		query: string;
		limit: number;
	};
	warnings: string[];
};

export const frequentDestinations: DestinationOption[] = [
	{ label: 'Miami, US', name: 'Miami', countryCode: 'US', lat: 25.7617, lon: -80.1918, type: 'preset' },
	{ label: 'Cancún, MX', name: 'Cancún', countryCode: 'MX', lat: 21.1619, lon: -86.8515, type: 'preset' },
	{ label: 'Madrid, ES', name: 'Madrid', countryCode: 'ES', lat: 40.4168, lon: -3.7038, type: 'preset' },
	{ label: 'Bali, ID', name: 'Bali', countryCode: 'ID', lat: -8.3405, lon: 115.092, type: 'preset' },
	{ label: 'Punta Cana, DO', name: 'Punta Cana', countryCode: 'DO', lat: 18.5601, lon: -68.3725, type: 'preset' },
	{
		label: 'Río de Janeiro, BR',
		name: 'Río de Janeiro',
		countryCode: 'BR',
		lat: -22.9068,
		lon: -43.1729,
		type: 'preset',
	},
];

const getApiUrl = (): string => process.env.REACT_APP_TRAVEL_INTELLIGENCE_API_URL ?? '';

/** Valida que una credencial no sea un valor vacío, nulo o los strings literales "undefined"/"null". */
const isValidCredential = (value: string | null | undefined): value is string => {
	if (!value) return false;
	const trimmed = value.trim();
	return trimmed !== '' && trimmed !== 'undefined' && trimmed !== 'null';
};

const travelIntelligenceApi: AxiosInstance = axios.create({
	headers: {
		'Content-Type': 'application/json',
	},
});

/**
 * Agrega X-Api-Key (Q_API_KEY) y X-Auth-Token (Q_TOKEN) a cada request de Travel Intelligence.
 * Usa las mismas credenciales de sesión que el resto del ecosistema.
 * Cancela el request si alguna credencial está ausente o es inválida.
 */
travelIntelligenceApi.interceptors.request.use((config: InternalAxiosRequestConfig) => {
	const apiKey = SessionService.getApiKey();
	const token = CookieService.getToken();

	if (!isValidCredential(apiKey)) {
		throw new Error('Travel Intelligence: API key de sesión no disponible. Iniciá sesión nuevamente.');
	}
	if (!isValidCredential(token)) {
		throw new Error('Travel Intelligence: token de sesión no disponible. Iniciá sesión nuevamente.');
	}

	config.headers['X-Api-Key'] = apiKey;
	config.headers['X-Auth-Token'] = token;

	return config;
});

const normalizeQueryValue = (value: unknown): string | null => {
	if (value === undefined || value === null || value === '') return null;
	if (Array.isArray(value) && value.length === 0) return null;
	if (typeof value === 'boolean') return value ? 'true' : 'false';
	return String(value);
};

export const buildQueryString = (query: Record<string, unknown> = {}): string => {
	const params = new URLSearchParams();

	Object.entries(query).forEach(([key, value]) => {
		if (Array.isArray(value)) {
			value.forEach((item) => {
				const normalized = normalizeQueryValue(item);
				if (normalized !== null) params.append(key, normalized);
			});
			return;
		}

		const normalized = normalizeQueryValue(value);
		if (normalized !== null) params.append(key, normalized);
	});

	return params.toString();
};

const normalizeError = (error: unknown): TravelIntelligenceError => {
	const axiosError = error as AxiosError<{ error?: TravelIntelligenceError; message?: string }>;
	const backendError = axiosError.response?.data?.error;

	if (backendError) {
		return {
			code: backendError.code,
			message: backendError.message || 'No se pudo completar la consulta.',
			details: backendError.details,
			status: axiosError.response?.status,
		};
	}

	return {
		message: axiosError.response?.data?.message || axiosError.message || 'No se pudo completar la consulta.',
		status: axiosError.response?.status,
	};
};

export const requestTravelIntelligence = async <TResponse = unknown>({
	method = 'GET',
	path,
	query,
	body,
}: TravelIntelligenceRequest): Promise<TResponse> => {
	const baseURL = getApiUrl();
	const queryString = buildQueryString(query);
	const url = `${path}${queryString ? `?${queryString}` : ''}`;

	if (!baseURL) {
		throw {
			message: 'Falta configurar REACT_APP_TRAVEL_INTELLIGENCE_API_URL.',
		} as TravelIntelligenceError;
	}

	if (!isValidCredential(SessionService.getApiKey())) {
		throw {
			message: 'Travel Intelligence: API key de sesión no disponible. Iniciá sesión nuevamente.',
		} as TravelIntelligenceError;
	}

	if (!isValidCredential(CookieService.getToken())) {
		throw {
			message: 'Travel Intelligence: token de sesión no disponible. Iniciá sesión nuevamente.',
		} as TravelIntelligenceError;
	}

	try {
		const response = await travelIntelligenceApi.request<TResponse>({
			baseURL,
			url,
			method,
			data: body,
		});
		return response.data;
	} catch (error) {
		throw normalizeError(error);
	}
};

const parseCoordinates = (value: string): DestinationOption | null => {
	const match = value.trim().match(/^(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)$/);
	if (!match) return null;

	const lat = Number(match[1]);
	const lon = Number(match[2]);
	if (Number.isNaN(lat) || Number.isNaN(lon)) return null;

	return {
		label: `${lat}, ${lon}`,
		name: `${lat}, ${lon}`,
		lat,
		lon,
		type: 'coordinates',
	};
};

export const searchLocationAutocomplete = async ({
	q,
	countryCodes,
	types = ['city'],
	limit = 10,
}: {
	q: string;
	countryCodes?: string[];
	types?: LocationAutocompleteType[];
	limit?: number;
}): Promise<LocationsAutocompleteResponse> =>
	requestTravelIntelligence<LocationsAutocompleteResponse>({
		path: '/travel-intelligence/locations/autocomplete',
		query: {
			q,
			countryCodes: countryCodes?.join(','),
			types: types.join(','),
			limit,
		},
	});

const mapLocationAutocompleteItemToDestination = (item: LocationAutocompleteItem): DestinationOption => ({
	label: `${item.name}${item.countryCode ? `, ${item.countryCode}` : ''}`,
	name: item.name,
	countryCode: item.countryCode,
	lat: item.lat,
	lon: item.lng,
	type: item.type,
	source: item.source,
	confidence: item.confidence,
});

export const geocodingClient = {
	searchDestinations: async (query: string): Promise<DestinationOption[]> => {
		const parsedCoordinates = parseCoordinates(query);
		const normalizedQuery = query.trim().toLowerCase();
		const presetMatches = frequentDestinations.filter((destination) =>
			[destination.label, destination.name, destination.countryCode]
				.filter(Boolean)
				.some((value) => value?.toLowerCase().includes(normalizedQuery)),
		);

		if (parsedCoordinates) return [parsedCoordinates, ...presetMatches];
		if (!normalizedQuery) return frequentDestinations;
		if (normalizedQuery.length < 2) return presetMatches;

		try {
			const response = await searchLocationAutocomplete({
				q: query.trim(),
				types: ['city'],
				limit: 10,
			});
			const apiDestinations = response.items.map(mapLocationAutocompleteItemToDestination);
			return [
				...apiDestinations,
				...presetMatches.filter(
					(preset) =>
						!apiDestinations.some(
							(destination) => destination.name === preset.name && destination.countryCode === preset.countryCode,
						),
				),
			];
		} catch {
			return presetMatches;
		}
	},
};
